import React from "react";
import { isOutOfRange } from "./legacyModals";
import { shortDate } from "./recsModel";
import { isTelevisit } from "./televisit";

/*
 * Patient Timeline (careiq-final): Follow up · since <last visit> at its top
 * (hidden while searching), then a "Last visit · <date>" divider and the
 * history up to and including that visit, grouped by year. Events after the
 * last visit that Follow up does not already show sit between the two; what
 * Follow up offers (including what was filed on the last visit day itself) is
 * never listed again in either part.
 * The lab sync stays lazy: only a search of 2+ characters or "Show all"
 * (the container's onSearch / onToggleExpand) can trigger it, never the open.
 */

export const TIMELINE_DOC_OPTIONS = [
  { key: "imagingresult", label: "Imaging results", hint: "X-ray, MRI, CT, ultrasound reports" },
  { key: "clinicaldocument", label: "Clinical documents", hint: "discharge summaries, referrals, outside records" },
  { key: "encounterdocument", label: "Progress / encounter notes", hint: "in-office visit notes (can be noisy)" },
];

const PILL = { visit: ["visit", "Visit"], televisit: ["visit", "Televisit"], lab: ["lab", "Lab"], imaging: ["imaging", "Imaging"], document: ["document", "Doc"] };

const GearIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

function matchedTests(ev, q) {
  if (!q || ev.type !== "lab" || !ev.raw || !ev.raw.tests) return null;
  const hits = ev.raw.tests.filter((t) => {
    const name = (t.testname || t.name || "").toLowerCase();
    const pn = (t.panelname || "").toLowerCase();
    return name.includes(q) || pn.includes(q);
  });
  if (!hits.length) return null;
  return hits.map((t) => {
    const refrange = t.refrange || (t.latestResult && t.latestResult.refrange) || "";
    const val = (t.latestResult && t.latestResult.value) || "—";
    return {
      name: t.testname || t.name || "",
      val,
      units: (t.latestResult && (t.latestResult.units || t.latestResult.unit)) || "",
      refrange,
      abnormal: isOutOfRange(t.latestResult && t.latestResult.value, refrange),
    };
  });
}

function YearGroups({ events, query, onEventClick }) {
  const groups = [];
  events.forEach((ev) => {
    const yr = ev.date.year();
    if (!groups.length || groups[groups.length - 1].year !== yr) groups.push({ year: yr, events: [] });
    groups[groups.length - 1].events.push(ev);
  });
  const q = (query || "").trim().toLowerCase();
  return groups.map((g) => (
    <div className="n3-tl-yg" key={g.year}>
      <div className="n3-tl-yh">{g.year}</div>
      {g.events.map((ev, i) => {
        const hits = matchedTests(ev, q);
        const [pc, pl] = (ev.type === "visit" && isTelevisit(ev.raw) ? PILL.televisit : PILL[ev.type]) || PILL.visit;
        return (
          <React.Fragment key={`${ev.type}-${ev.date.valueOf()}-${i}`}>
            <button type="button" className="n3-tl-row no-hover-tint" onClick={() => onEventClick(ev)}>
              <span className={`n3-tl-dot n3-dot-${ev.dotColor}`} />
              <span className="n3-tl-date">{ev.dateStr}</span>
              <span className="n3-tl-lbl" title={ev.label}>{ev.label}</span>
              <span className={`n3-tl-pill n3-pill-${pc}`}>
                {pl}
                {ev.type === "visit" && ev.hasHandwritten ? (
                  <span className="n3-tl-hw" title="Handwritten note on file" aria-label="Handwritten note on file">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                  </span>
                ) : null}
              </span>
            </button>
            {hits && (
              <div className="n3-tl-hits">
                {hits.map((mt, j) => (
                  <div key={j} className={`n3-tl-hit${mt.abnormal ? " abn" : ""}`}>
                    <b>{mt.name}</b>
                    <span className="v">{mt.val}</span>
                    {mt.units ? <span className="u">{mt.units}</span> : null}
                    {mt.refrange ? <span className="u">({mt.refrange})</span> : null}
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  ));
}

export default function PatientTimeline({
  sidebarRef, sidebarStyle, scrollRef, events, search, onSearch, loading, followUp, lastVisitDate, dosYear,
  coveredByFollowUp, expanded, onToggleExpand, onEventClick, prefs, dosKey, asOfLine,
}) {
  const searching = Boolean((search || "").trim());
  const q = (search || "").trim().toLowerCase();
  const filtered = searching
    ? events.filter((ev) => ev.label.toLowerCase().includes(q) || ev.dateStr.toLowerCase().includes(q) || ev.type.includes(q) || (ev.type === "visit" && "televisit".includes(q) && isTelevisit(ev.raw)) || (ev.searchText && ev.searchText.toLowerCase().includes(q)))
    : events;
  const lvKey = lastVisitDate || null;
  // after the last visit: what Follow up does not show (and not this visit itself: the DOS row is it)
  const after = !searching && lvKey
    ? filtered.filter((ev) => ev.date.format("YYYY-MM-DD") > lvKey && !coveredByFollowUp(ev) && !(ev.type === "visit" && ev.date.format("YYYY-MM-DD") === dosKey))
    : [];
  // the history: never what Follow up offers either. Follow up's window starts ON the last visit day
  // (a result or an ECG filed at that visit is a Follow up row), so the history up to and including
  // that day hides it too (D2, review 10-06: 238 of 1,248 9928 charts listed such a document twice)
  const history = !searching && lvKey
    ? filtered.filter((ev) => ev.date.format("YYYY-MM-DD") <= lvKey && !coveredByFollowUp(ev))
    : filtered.filter((ev) => searching || !coveredByFollowUp(ev));
  const visible = expanded ? history : history.slice(0, 15);
  return (
    <aside className="n3-sidebar" ref={sidebarRef} style={sidebarStyle} data-testid="n3-sidebar">
      <div className="n3-tl-panel">
        <div className="n3-tl-header">
          <span aria-hidden="true">🕐</span> Patient Timeline
          {loading ? <span className="n3-spin" aria-label="Loading" /> : null}
          <button type="button" className="n3-tl-gear no-hover-tint" title="Choose which documents appear on the timeline" aria-label="Timeline document preferences" onClick={prefs.onOpen}>
            <GearIcon />
          </button>
        </div>
        {prefs.open && (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center" }}
            onClick={prefs.onClose}
          >
            <div
              role="dialog"
              aria-label="Timeline documents"
              style={{ background: "#fff", color: "#1B2A4A", borderRadius: "12px", padding: "18px 20px", width: "360px", maxWidth: "calc(100vw - 32px)", boxShadow: "0 10px 40px rgba(0,0,0,0.22)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "4px" }}>Timeline documents</div>
              <p style={{ fontSize: "11px", color: "#64748B", margin: "0 0 12px", lineHeight: 1.5 }}>
                Choose which document types appear on your timeline. Saved to your profile — applies wherever you log in.
              </p>
              {TIMELINE_DOC_OPTIONS.map((opt) => (
                <label key={opt.key} style={{ display: "flex", alignItems: "flex-start", gap: "8px", padding: "7px 8px", borderRadius: "8px", cursor: "pointer", background: prefs.draft.includes(opt.key) ? "#FFF3EF" : "transparent", marginBottom: "4px" }}>
                  <input
                    type="checkbox"
                    checked={prefs.draft.includes(opt.key)}
                    onChange={(e) => prefs.setDraft(e.target.checked ? [...prefs.draft, opt.key] : prefs.draft.filter((k) => k !== opt.key))}
                    style={{ marginTop: "2px", accentColor: "#E8653A" }}
                  />
                  <span>
                    <span style={{ display: "block", fontSize: "12px", fontWeight: 600 }}>{opt.label}</span>
                    <span style={{ display: "block", fontSize: "10.5px", color: "#94A3B8" }}>{opt.hint}</span>
                  </span>
                </label>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
                <button type="button" onClick={prefs.onClose} style={{ border: "1px solid #E2E8F0", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", fontWeight: 600, color: "#64748B" }}>Cancel</button>
                <button
                  type="button"
                  onClick={prefs.onSave}
                  disabled={prefs.saving || prefs.draft.length === 0}
                  style={{ background: prefs.draft.length === 0 ? "#F1F5F9" : "linear-gradient(135deg, #E8653A, #D4552B)", borderRadius: "8px", padding: "6px 16px", fontSize: "12px", fontWeight: 700, color: prefs.draft.length === 0 ? "#94A3B8" : "#fff" }}
                >
                  {prefs.saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
        {asOfLine ? <div className="n3-tl-asof" data-testid="n3-tl-asof">{asOfLine}</div> : null}
        <div className="n3-tl-search">
          <input type="text" placeholder="Search timeline… (e.g. A1C, CBC)" aria-label="Search timeline" value={search} onChange={(e) => onSearch(e.target.value)} />
        </div>
        <div className="n3-tl-scroll" ref={scrollRef}>
          {!searching && followUp ? <div className="n3-since-side">{followUp}</div> : null}
          {after.length ? <YearGroups events={after} query="" onEventClick={onEventClick} /> : null}
          {!searching && lvKey ? <div className="n3-tl-div">Last visit · {shortDate(lvKey, dosYear)}</div> : null}
          {visible.length ? (
            <YearGroups events={visible} query={q} onEventClick={onEventClick} />
          ) : (
            <div className="n3-tl-empty">{searching ? "No events found" : loading ? "Loading…" : "No earlier events"}</div>
          )}
          {history.length > 15 && (
            <div className="n3-tl-more">
              <button type="button" className="no-hover-tint" onClick={onToggleExpand}>
                {expanded ? "Show less ↑" : `Show all (${history.length}) ↓`}
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
